import { useState, useEffect } from "react";

interface ManagedArticle {
  id: string;
  title: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
  shortDesc: string;
  content: string;
  image: string;
  tags: string;
  featured: boolean;
  createdAt: string;
}

interface ManagedPlace {
  id: string;
  name: string;
  city: string;
  category: string;
  shortDesc: string;
  description: string;
  image: string;
  tags: string;
  createdAt: string;
}

interface ManagedCity {
  id: string;
  name: string;
  region: string;
  shortDesc: string;
  description: string;
  image: string;
  highlights: string;
  bestSeason: string;
  featured: boolean;
  createdAt: string;
}

const ARTICLES_KEY = "adminArticles";
const PLACES_KEY = "adminPlaces";
const CITIES_KEY = "adminCities";

const ARTICLE_CATEGORIES = [
  "Gezi Rehberi", "Keşif", "Gastronomi", "Doğa",
  "Tarih & Arkeoloji", "Şehir Rehberi", "Ada & Deniz"
];

const PLACE_CATEGORIES = [
  "Tarihi Yer", "Doğa", "Plaj", "Müze", "Şelale",
  "Kanyon", "Yayla", "Antik Kent", "Köy", "Diğer"
];

const CITIES = [
  "İstanbul", "Ankara", "İzmir", "Antalya", "Bursa", "Gaziantep",
  "Konya", "Trabzon", "Mardin", "Nevşehir", "Bodrum", "Alaçatı",
  "Fethiye", "Van", "Diyarbakır", "Edirne", "Kars", "Sakarya",
  "Muğla", "Denizli", "Şanlıurfa", "Adıyaman", "Diğer"
];

const REGIONS = [
  "Karadeniz", "Ege", "Akdeniz", "İç Anadolu",
  "Doğu Anadolu", "Güneydoğu Anadolu", "Marmara"
];

const BEST_SEASONS = [
  "İlkbahar", "Yaz", "Sonbahar", "Kış",
  "İlkbahar & Yaz", "Yaz & Sonbahar", "Tüm Yıl"
];

type Tab = "articles" | "places" | "cities";

const ContentManager = () => {
  const [activeTab, setActiveTab] = useState<Tab>("articles");
  const [articles, setArticles] = useState<ManagedArticle[]>([]);
  const [places, setPlaces] = useState<ManagedPlace[]>([]);
  const [cities, setCities] = useState<ManagedCity[]>([]);
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [showPlaceForm, setShowPlaceForm] = useState(false);
  const [showCityForm, setShowCityForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState<ManagedArticle | null>(null);
  const [editingPlace, setEditingPlace] = useState<ManagedPlace | null>(null);
  const [editingCity, setEditingCity] = useState<ManagedCity | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const [articleForm, setArticleForm] = useState({
    title: "", category: "Gezi Rehberi", author: "", readTime: "10 dk",
    shortDesc: "", content: "", image: "", tags: "", featured: false,
  });

  const [placeForm, setPlaceForm] = useState({
    name: "", city: "İstanbul", category: "Tarihi Yer",
    shortDesc: "", description: "", image: "", tags: "",
  });

  const [cityForm, setCityForm] = useState({
    name: "",
    region: "Karadeniz",
    shortDesc: "",
    description: "",
    image: "",
    highlights: "",
    bestSeason: "Tüm Yıl",
    featured: false,
  });

  const [articleErrors, setArticleErrors] = useState<Record<string, string>>({});
  const [placeErrors, setPlaceErrors] = useState<Record<string, string>>({});
  const [cityErrors, setCityErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setArticles(JSON.parse(localStorage.getItem(ARTICLES_KEY) || "[]"));
    setPlaces(JSON.parse(localStorage.getItem(PLACES_KEY) || "[]"));
    setCities(JSON.parse(localStorage.getItem(CITIES_KEY) || "[]"));
  }, []);

  const showToastMsg = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  // --- ARTICLE CRUD ---
  const validateArticle = () => {
    const e: Record<string, string> = {};
    if (!articleForm.title.trim()) e.title = "Başlık zorunludur";
    if (!articleForm.author.trim()) e.author = "Yazar zorunludur";
    if (!articleForm.shortDesc.trim()) e.shortDesc = "Kısa açıklama zorunludur";
    if (!articleForm.content.trim()) e.content = "İçerik zorunludur";
    setArticleErrors(e);
    return Object.keys(e).length === 0;
  };

  const saveArticle = () => {
    if (!validateArticle()) return;
    const now = new Date().toISOString();
    const slug = articleForm.title.toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9\-]/g, "")
      .substring(0, 60);

    if (editingArticle) {
      const updated = articles.map((a) =>
        a.id === editingArticle.id
          ? { ...editingArticle, ...articleForm, id: editingArticle.id, createdAt: editingArticle.createdAt, date: editingArticle.date }
          : a
      );
      setArticles(updated);
      localStorage.setItem(ARTICLES_KEY, JSON.stringify(updated));
      showToastMsg("Makale güncellendi!");
    } else {
      const newArticle: ManagedArticle = {
        id: `admin-${slug}-${Date.now()}`,
        ...articleForm,
        date: now.split("T")[0],
        createdAt: now,
      };
      const updated = [newArticle, ...articles];
      setArticles(updated);
      localStorage.setItem(ARTICLES_KEY, JSON.stringify(updated));
      showToastMsg("Makale eklendi!");
    }
    resetArticleForm();
  };

  const resetArticleForm = () => {
    setArticleForm({ title: "", category: "Gezi Rehberi", author: "", readTime: "10 dk", shortDesc: "", content: "", image: "", tags: "", featured: false });
    setArticleErrors({});
    setShowArticleForm(false);
    setEditingArticle(null);
  };

  const editArticle = (a: ManagedArticle) => {
    setArticleForm({ title: a.title, category: a.category, author: a.author, readTime: a.readTime, shortDesc: a.shortDesc, content: a.content, image: a.image, tags: a.tags, featured: a.featured });
    setEditingArticle(a);
    setShowArticleForm(true);
    setArticleErrors({});
  };

  const deleteArticle = (id: string) => {
    const updated = articles.filter((a) => a.id !== id);
    setArticles(updated);
    localStorage.setItem(ARTICLES_KEY, JSON.stringify(updated));
    setDeleteConfirm(null);
    showToastMsg("Makale silindi.");
  };

  // --- PLACE CRUD ---
  const validatePlace = () => {
    const e: Record<string, string> = {};
    if (!placeForm.name.trim()) e.name = "Yer adı zorunludur";
    if (!placeForm.shortDesc.trim()) e.shortDesc = "Kısa açıklama zorunludur";
    if (!placeForm.description.trim()) e.description = "Açıklama zorunludur";
    setPlaceErrors(e);
    return Object.keys(e).length === 0;
  };

  const savePlace = () => {
    if (!validatePlace()) return;
    const now = new Date().toISOString();
    const slug = placeForm.name.toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9\-]/g, "")
      .substring(0, 60);

    if (editingPlace) {
      const updated = places.map((p) =>
        p.id === editingPlace.id ? { ...editingPlace, ...placeForm } : p
      );
      setPlaces(updated);
      localStorage.setItem(PLACES_KEY, JSON.stringify(updated));
      showToastMsg("Yer güncellendi!");
    } else {
      const newPlace: ManagedPlace = {
        id: `place-${slug}-${Date.now()}`,
        ...placeForm,
        createdAt: now,
      };
      const updated = [newPlace, ...places];
      setPlaces(updated);
      localStorage.setItem(PLACES_KEY, JSON.stringify(updated));
      showToastMsg("Yer eklendi!");
    }
    resetPlaceForm();
  };

  const resetPlaceForm = () => {
    setPlaceForm({ name: "", city: "İstanbul", category: "Tarihi Yer", shortDesc: "", description: "", image: "", tags: "" });
    setPlaceErrors({});
    setShowPlaceForm(false);
    setEditingPlace(null);
  };

  const editPlace = (p: ManagedPlace) => {
    setPlaceForm({ name: p.name, city: p.city, category: p.category, shortDesc: p.shortDesc, description: p.description, image: p.image, tags: p.tags });
    setEditingPlace(p);
    setShowPlaceForm(true);
    setPlaceErrors({});
  };

  const deletePlace = (id: string) => {
    const updated = places.filter((p) => p.id !== id);
    setPlaces(updated);
    localStorage.setItem(PLACES_KEY, JSON.stringify(updated));
    setDeleteConfirm(null);
    showToastMsg("Yer silindi.");
  };

  // --- CITY CRUD ---
  const validateCity = () => {
    const e: Record<string, string> = {};
    if (!cityForm.name.trim()) e.name = "Şehir/bölge adı zorunludur";
    if (!cityForm.shortDesc.trim()) e.shortDesc = "Kısa açıklama zorunludur";
    if (!cityForm.description.trim()) e.description = "Açıklama zorunludur";
    setCityErrors(e);
    return Object.keys(e).length === 0;
  };

  const saveCity = () => {
    if (!validateCity()) return;
    const now = new Date().toISOString();
    const slug = cityForm.name.toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9\-]/g, "")
      .substring(0, 60);

    if (editingCity) {
      const updated = cities.map((c) =>
        c.id === editingCity.id ? { ...editingCity, ...cityForm } : c
      );
      setCities(updated);
      localStorage.setItem(CITIES_KEY, JSON.stringify(updated));
      showToastMsg("Şehir/bölge güncellendi!");
    } else {
      const newCity: ManagedCity = {
        id: `city-${slug}-${Date.now()}`,
        ...cityForm,
        createdAt: now,
      };
      const updated = [newCity, ...cities];
      setCities(updated);
      localStorage.setItem(CITIES_KEY, JSON.stringify(updated));
      showToastMsg("Şehir/bölge eklendi!");
    }
    resetCityForm();
  };

  const resetCityForm = () => {
    setCityForm({ name: "", region: "Karadeniz", shortDesc: "", description: "", image: "", highlights: "", bestSeason: "Tüm Yıl", featured: false });
    setCityErrors({});
    setShowCityForm(false);
    setEditingCity(null);
  };

  const editCity = (c: ManagedCity) => {
    setCityForm({ name: c.name, region: c.region, shortDesc: c.shortDesc, description: c.description, image: c.image, highlights: c.highlights, bestSeason: c.bestSeason, featured: c.featured });
    setEditingCity(c);
    setShowCityForm(true);
    setCityErrors({});
  };

  const deleteCity = (id: string) => {
    const updated = cities.filter((c) => c.id !== id);
    setCities(updated);
    localStorage.setItem(CITIES_KEY, JSON.stringify(updated));
    setDeleteConfirm(null);
    showToastMsg("Şehir/bölge silindi.");
  };

  const inputClass = (err?: string) =>
    `w-full border rounded-lg px-3 py-2.5 text-sm text-stone-800 outline-none transition-colors ${err ? "border-rose-400 bg-rose-50" : "border-stone-200 focus:border-amber-400"}`;

  return (
    <div className="relative">
      {/* Toast */}
      {toast && (
        <div className="fixed top-20 right-4 z-50 bg-emerald-500 text-white px-4 py-3 rounded-xl text-sm font-medium flex items-center gap-2">
          <i className="ri-checkbox-circle-line"></i> {toast}
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 flex items-center justify-center bg-rose-100 rounded-full mx-auto mb-4">
              <i className="ri-delete-bin-line text-rose-600 text-xl"></i>
            </div>
            <h3 className="text-base font-bold text-stone-900 text-center mb-2">Silmek istediğinize emin misiniz?</h3>
            <p className="text-stone-500 text-sm text-center mb-5">Bu işlem geri alınamaz.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 border border-stone-200 text-stone-600 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-stone-50 transition-colors">İptal</button>
              <button
                onClick={() => {
                  if (deleteConfirm.startsWith("article-")) deleteArticle(deleteConfirm.replace("article-", ""));
                  else if (deleteConfirm.startsWith("place-")) deletePlace(deleteConfirm.replace("place-", ""));
                  else if (deleteConfirm.startsWith("city-")) deleteCity(deleteConfirm.replace("city-", ""));
                }}
                className="flex-1 py-2.5 bg-rose-500 text-white rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-rose-600 transition-colors"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            İçerik Yönetimi
          </h2>
          <p className="text-stone-400 text-xs mt-0.5">Makale, yer ve şehir/bölge içeriklerini yönetin</p>
        </div>
        <div className="flex items-center bg-stone-100 rounded-full p-1 gap-1">
          <button
            onClick={() => { setActiveTab("articles"); setShowArticleForm(false); setShowPlaceForm(false); setShowCityForm(false); }}
            className={`text-xs px-4 py-2 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap
              ${activeTab === "articles" ? "bg-white text-stone-800" : "text-stone-500 hover:text-stone-700"}`}
          >
            <i className="ri-article-line mr-1"></i>Makaleler ({articles.length})
          </button>
          <button
            onClick={() => { setActiveTab("places"); setShowArticleForm(false); setShowPlaceForm(false); setShowCityForm(false); }}
            className={`text-xs px-4 py-2 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap
              ${activeTab === "places" ? "bg-white text-stone-800" : "text-stone-500 hover:text-stone-700"}`}
          >
            <i className="ri-map-pin-line mr-1"></i>Yerler ({places.length})
          </button>
          <button
            onClick={() => { setActiveTab("cities"); setShowArticleForm(false); setShowPlaceForm(false); setShowCityForm(false); }}
            className={`text-xs px-4 py-2 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap
              ${activeTab === "cities" ? "bg-white text-stone-800" : "text-stone-500 hover:text-stone-700"}`}
          >
            <i className="ri-building-4-line mr-1"></i>Şehirler & Bölgeler ({cities.length})
          </button>
        </div>
      </div>

      {/* ARTICLES TAB */}
      {activeTab === "articles" && (
        <div>
          {/* Add Button */}
          {!showArticleForm && (
            <button
              onClick={() => { setShowArticleForm(true); setEditingArticle(null); setArticleForm({ title: "", category: "Gezi Rehberi", author: "", readTime: "10 dk", shortDesc: "", content: "", image: "", tags: "", featured: false }); }}
              className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap mb-5"
            >
              <i className="ri-add-line"></i> Yeni Makale Ekle
            </button>
          )}

          {/* Article Form */}
          {showArticleForm && (
            <div className="bg-white rounded-2xl border border-stone-100 p-6 mb-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-bold text-stone-900 text-base">
                  {editingArticle ? "Makaleyi Düzenle" : "Yeni Makale Ekle"}
                </h3>
                <button onClick={resetArticleForm} className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer">
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Başlık *</label>
                    <input type="text" value={articleForm.title} onChange={(e) => setArticleForm((p) => ({ ...p, title: e.target.value }))} placeholder="Makale başlığı..." className={inputClass(articleErrors.title)} />
                    {articleErrors.title && <p className="text-rose-500 text-xs mt-1">{articleErrors.title}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Kategori</label>
                    <select value={articleForm.category} onChange={(e) => setArticleForm((p) => ({ ...p, category: e.target.value }))} className={inputClass()}>
                      {ARTICLE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Yazar *</label>
                    <input type="text" value={articleForm.author} onChange={(e) => setArticleForm((p) => ({ ...p, author: e.target.value }))} placeholder="Yazar adı" className={inputClass(articleErrors.author)} />
                    {articleErrors.author && <p className="text-rose-500 text-xs mt-1">{articleErrors.author}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Okuma Süresi</label>
                    <input type="text" value={articleForm.readTime} onChange={(e) => setArticleForm((p) => ({ ...p, readTime: e.target.value }))} placeholder="Örn: 10 dk" className={inputClass()} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Etiketler</label>
                    <input type="text" value={articleForm.tags} onChange={(e) => setArticleForm((p) => ({ ...p, tags: e.target.value }))} placeholder="Virgülle ayırın: İstanbul, Tarih" className={inputClass()} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Kapak Görseli URL</label>
                    <input type="text" value={articleForm.image} onChange={(e) => setArticleForm((p) => ({ ...p, image: e.target.value }))} placeholder="https://..." className={inputClass()} />
                    {articleForm.image && (
                      <div className="mt-2 w-full h-32 rounded-lg overflow-hidden border border-stone-200">
                        <img src={articleForm.image} alt="Önizleme" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                      </div>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Kısa Açıklama * <span className="text-stone-400 font-normal">({articleForm.shortDesc.length}/200)</span></label>
                    <textarea value={articleForm.shortDesc} onChange={(e) => { if (e.target.value.length <= 200) setArticleForm((p) => ({ ...p, shortDesc: e.target.value })); }} rows={2} placeholder="Makaleyi özetleyen 1-2 cümle..." className={`${inputClass(articleErrors.shortDesc)} resize-none`} />
                    {articleErrors.shortDesc && <p className="text-rose-500 text-xs mt-1">{articleErrors.shortDesc}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                      İçerik * <span className="text-stone-400 font-normal">({articleForm.content.length}/5000 karakter) — ## ile bölüm başlığı, **metin** ile kalın yazı</span>
                    </label>
                    <textarea
                      value={articleForm.content}
                      onChange={(e) => { if (e.target.value.length <= 5000) setArticleForm((p) => ({ ...p, content: e.target.value })); }}
                      rows={12}
                      placeholder={`## Bölüm Başlığı\n\nParagraf metni buraya...\n\n**Kalın başlık**\n\nDevam eden metin...`}
                      className={`${inputClass(articleErrors.content)} resize-none font-mono text-xs`}
                    />
                    {articleErrors.content && <p className="text-rose-500 text-xs mt-1">{articleErrors.content}</p>}
                  </div>
                  <div className="sm:col-span-2 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setArticleForm((p) => ({ ...p, featured: !p.featured }))}
                      className={`flex items-center gap-2 text-xs px-4 py-2 rounded-full border transition-colors cursor-pointer whitespace-nowrap
                        ${articleForm.featured ? "bg-amber-500 text-white border-amber-500" : "border-stone-200 text-stone-500 hover:border-stone-300"}`}
                    >
                      <i className={articleForm.featured ? "ri-star-fill" : "ri-star-line"}></i>
                      {articleForm.featured ? "Öne Çıkan" : "Öne Çıkar"}
                    </button>
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={saveArticle} className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white rounded-full text-sm font-medium hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-save-line"></i> {editingArticle ? "Güncelle" : "Kaydet"}
                  </button>
                  <button onClick={resetArticleForm} className="px-5 py-2.5 border border-stone-200 text-stone-600 rounded-full text-sm font-medium hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap">
                    İptal
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Articles List */}
          {articles.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-stone-100">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 bg-stone-100 rounded-full">
                <i className="ri-article-line text-stone-400 text-2xl"></i>
              </div>
              <p className="text-stone-500 font-medium text-sm">Henüz makale eklenmedi</p>
              <p className="text-stone-400 text-xs mt-1">Yukarıdaki butona tıklayarak ilk makalenizi ekleyin</p>
            </div>
          ) : (
            <div className="space-y-3">
              {articles.map((article) => (
                <div key={article.id} className="bg-white rounded-xl border border-stone-100 p-4 flex items-start gap-4 hover:border-stone-200 transition-colors">
                  {article.image && (
                    <div className="w-20 h-14 flex-shrink-0 rounded-lg overflow-hidden border border-stone-100">
                      <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">{article.category}</span>
                          {article.featured && <span className="text-xs bg-stone-900 text-white px-2 py-0.5 rounded-full">Öne Çıkan</span>}
                        </div>
                        <h4 className="font-semibold text-stone-900 text-sm line-clamp-1">{article.title}</h4>
                        <p className="text-stone-400 text-xs mt-0.5 line-clamp-1">{article.shortDesc}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-stone-400">
                          <span>{article.author}</span>
                          <span>{article.readTime}</span>
                          <span>{new Date(article.createdAt).toLocaleDateString("tr-TR")}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => editArticle(article)} className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer">
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button onClick={() => setDeleteConfirm(`article-${article.id}`)} className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer">
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* PLACES TAB */}
      {activeTab === "places" && (
        <div>
          {!showPlaceForm && (
            <button
              onClick={() => { setShowPlaceForm(true); setEditingPlace(null); setPlaceForm({ name: "", city: "İstanbul", category: "Tarihi Yer", shortDesc: "", description: "", image: "", tags: "" }); }}
              className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap mb-5"
            >
              <i className="ri-add-line"></i> Yeni Yer Ekle
            </button>
          )}

          {showPlaceForm && (
            <div className="bg-white rounded-2xl border border-stone-100 p-6 mb-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-bold text-stone-900 text-base">
                  {editingPlace ? "Yeri Düzenle" : "Yeni Yer Ekle"}
                </h3>
                <button onClick={resetPlaceForm} className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer">
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Yer Adı *</label>
                    <input type="text" value={placeForm.name} onChange={(e) => setPlaceForm((p) => ({ ...p, name: e.target.value }))} placeholder="Yer adı..." className={inputClass(placeErrors.name)} />
                    {placeErrors.name && <p className="text-rose-500 text-xs mt-1">{placeErrors.name}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Şehir</label>
                    <select value={placeForm.city} onChange={(e) => setPlaceForm((p) => ({ ...p, city: e.target.value }))} className={inputClass()}>
                      {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Kategori</label>
                    <select value={placeForm.category} onChange={(e) => setPlaceForm((p) => ({ ...p, category: e.target.value }))} className={inputClass()}>
                      {PLACE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Etiketler</label>
                    <input type="text" value={placeForm.tags} onChange={(e) => setPlaceForm((p) => ({ ...p, tags: e.target.value }))} placeholder="Virgülle ayırın" className={inputClass()} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Görsel URL</label>
                    <input type="text" value={placeForm.image} onChange={(e) => setPlaceForm((p) => ({ ...p, image: e.target.value }))} placeholder="https://..." className={inputClass()} />
                    {placeForm.image && (
                      <div className="mt-2 w-full h-28 rounded-lg overflow-hidden border border-stone-200">
                        <img src={placeForm.image} alt="Önizleme" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                      </div>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Kısa Açıklama * <span className="text-stone-400 font-normal">({placeForm.shortDesc.length}/200)</span></label>
                    <textarea value={placeForm.shortDesc} onChange={(e) => { if (e.target.value.length <= 200) setPlaceForm((p) => ({ ...p, shortDesc: e.target.value })); }} rows={2} placeholder="Yeri özetleyen kısa açıklama..." className={`${inputClass(placeErrors.shortDesc)} resize-none`} />
                    {placeErrors.shortDesc && <p className="text-rose-500 text-xs mt-1">{placeErrors.shortDesc}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Detaylı Açıklama * <span className="text-stone-400 font-normal">({placeForm.description.length}/500)</span></label>
                    <textarea value={placeForm.description} onChange={(e) => { if (e.target.value.length <= 500) setPlaceForm((p) => ({ ...p, description: e.target.value })); }} rows={5} placeholder="Yer hakkında detaylı bilgi..." className={`${inputClass(placeErrors.description)} resize-none`} />
                    {placeErrors.description && <p className="text-rose-500 text-xs mt-1">{placeErrors.description}</p>}
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={savePlace} className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white rounded-full text-sm font-medium hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-save-line"></i> {editingPlace ? "Güncelle" : "Kaydet"}
                  </button>
                  <button onClick={resetPlaceForm} className="px-5 py-2.5 border border-stone-200 text-stone-600 rounded-full text-sm font-medium hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap">
                    İptal
                  </button>
                </div>
              </div>
            </div>
          )}

          {places.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-stone-100">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 bg-stone-100 rounded-full">
                <i className="ri-map-pin-line text-stone-400 text-2xl"></i>
              </div>
              <p className="text-stone-500 font-medium text-sm">Henüz yer eklenmedi</p>
              <p className="text-stone-400 text-xs mt-1">Yukarıdaki butona tıklayarak ilk yeri ekleyin</p>
            </div>
          ) : (
            <div className="space-y-3">
              {places.map((place) => (
                <div key={place.id} className="bg-white rounded-xl border border-stone-100 p-4 flex items-start gap-4 hover:border-stone-200 transition-colors">
                  {place.image && (
                    <div className="w-20 h-14 flex-shrink-0 rounded-lg overflow-hidden border border-stone-100">
                      <img src={place.image} alt={place.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">{place.category}</span>
                          <span className="text-xs text-stone-400">{place.city}</span>
                        </div>
                        <h4 className="font-semibold text-stone-900 text-sm line-clamp-1">{place.name}</h4>
                        <p className="text-stone-400 text-xs mt-0.5 line-clamp-1">{place.shortDesc}</p>
                        <p className="text-xs text-stone-300 mt-0.5">{new Date(place.createdAt).toLocaleDateString("tr-TR")}</p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => editPlace(place)} className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer">
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button onClick={() => setDeleteConfirm(`place-${place.id}`)} className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer">
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CITIES TAB */}
      {activeTab === "cities" && (
        <div>
          {!showCityForm && (
            <button
              onClick={() => {
                setShowCityForm(true);
                setEditingCity(null);
                setCityForm({ name: "", region: "Karadeniz", shortDesc: "", description: "", image: "", highlights: "", bestSeason: "Tüm Yıl", featured: false });
              }}
              className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap mb-5"
            >
              <i className="ri-add-line"></i> Yeni Şehir / Bölge Ekle
            </button>
          )}

          {showCityForm && (
            <div className="bg-white rounded-2xl border border-stone-100 p-6 mb-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-bold text-stone-900 text-base">
                  {editingCity ? "Şehir / Bölgeyi Düzenle" : "Yeni Şehir / Bölge Ekle"}
                </h3>
                <button onClick={resetCityForm} className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer">
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Şehir / Bölge Adı *</label>
                    <input
                      type="text"
                      value={cityForm.name}
                      onChange={(e) => setCityForm((p) => ({ ...p, name: e.target.value }))}
                      placeholder="Örn: Trabzon, Karadeniz Bölgesi..."
                      className={inputClass(cityErrors.name)}
                    />
                    {cityErrors.name && <p className="text-rose-500 text-xs mt-1">{cityErrors.name}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Bölge</label>
                    <select
                      value={cityForm.region}
                      onChange={(e) => setCityForm((p) => ({ ...p, region: e.target.value }))}
                      className={inputClass()}
                    >
                      {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">En İyi Sezon</label>
                    <select
                      value={cityForm.bestSeason}
                      onChange={(e) => setCityForm((p) => ({ ...p, bestSeason: e.target.value }))}
                      className={inputClass()}
                    >
                      {BEST_SEASONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Öne Çıkan Yerler</label>
                    <input
                      type="text"
                      value={cityForm.highlights}
                      onChange={(e) => setCityForm((p) => ({ ...p, highlights: e.target.value }))}
                      placeholder="Virgülle ayırın: Ayasofya, Kapalıçarşı..."
                      className={inputClass()}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Kapak Görseli URL</label>
                    <input
                      type="text"
                      value={cityForm.image}
                      onChange={(e) => setCityForm((p) => ({ ...p, image: e.target.value }))}
                      placeholder="https://..."
                      className={inputClass()}
                    />
                    {cityForm.image && (
                      <div className="mt-2 w-full h-32 rounded-lg overflow-hidden border border-stone-200">
                        <img src={cityForm.image} alt="Önizleme" className="w-full h-full object-cover object-top" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                      </div>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                      Kısa Açıklama * <span className="text-stone-400 font-normal">({cityForm.shortDesc.length}/200)</span>
                    </label>
                    <textarea
                      value={cityForm.shortDesc}
                      onChange={(e) => { if (e.target.value.length <= 200) setCityForm((p) => ({ ...p, shortDesc: e.target.value })); }}
                      rows={2}
                      placeholder="Şehri/bölgeyi özetleyen 1-2 cümle..."
                      className={`${inputClass(cityErrors.shortDesc)} resize-none`}
                    />
                    {cityErrors.shortDesc && <p className="text-rose-500 text-xs mt-1">{cityErrors.shortDesc}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                      Detaylı Açıklama * <span className="text-stone-400 font-normal">({cityForm.description.length}/500)</span>
                    </label>
                    <textarea
                      value={cityForm.description}
                      onChange={(e) => { if (e.target.value.length <= 500) setCityForm((p) => ({ ...p, description: e.target.value })); }}
                      rows={5}
                      placeholder="Şehir veya bölge hakkında detaylı bilgi, tarihi, kültürü, doğası..."
                      className={`${inputClass(cityErrors.description)} resize-none`}
                    />
                    {cityErrors.description && <p className="text-rose-500 text-xs mt-1">{cityErrors.description}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <button
                      type="button"
                      onClick={() => setCityForm((p) => ({ ...p, featured: !p.featured }))}
                      className={`flex items-center gap-2 text-xs px-4 py-2 rounded-full border transition-colors cursor-pointer whitespace-nowrap
                        ${cityForm.featured ? "bg-amber-500 text-white border-amber-500" : "border-stone-200 text-stone-500 hover:border-stone-300"}`}
                    >
                      <i className={cityForm.featured ? "ri-star-fill" : "ri-star-line"}></i>
                      {cityForm.featured ? "Öne Çıkan" : "Öne Çıkar"}
                    </button>
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={saveCity}
                    className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white rounded-full text-sm font-medium hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-save-line"></i> {editingCity ? "Güncelle" : "Kaydet"}
                  </button>
                  <button
                    onClick={resetCityForm}
                    className="px-5 py-2.5 border border-stone-200 text-stone-600 rounded-full text-sm font-medium hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    İptal
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Region Filter */}
          {cities.length > 0 && !showCityForm && (
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {["Tümü", ...REGIONS].map((r) => {
                const count = r === "Tümü" ? cities.length : cities.filter((c) => c.region === r).length;
                if (count === 0 && r !== "Tümü") return null;
                return (
                  <span key={r} className="text-xs px-3 py-1.5 rounded-full bg-stone-100 text-stone-600 font-medium">
                    {r} {count > 0 && <span className="ml-1 text-stone-400">({count})</span>}
                  </span>
                );
              })}
            </div>
          )}

          {cities.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-stone-100">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 bg-stone-100 rounded-full">
                <i className="ri-building-4-line text-stone-400 text-2xl"></i>
              </div>
              <p className="text-stone-500 font-medium text-sm">Henüz şehir / bölge eklenmedi</p>
              <p className="text-stone-400 text-xs mt-1">Yukarıdaki butona tıklayarak ilk şehri ekleyin</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {cities.map((city) => (
                <div key={city.id} className="bg-white rounded-xl border border-stone-100 overflow-hidden hover:border-stone-200 transition-colors group">
                  {city.image ? (
                    <div className="w-full h-36 overflow-hidden relative">
                      <img src={city.image} alt={city.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                      <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                        <div>
                          <p className="text-white font-bold text-sm">{city.name}</p>
                          <p className="text-white/70 text-xs">{city.region}</p>
                        </div>
                        {city.featured && (
                          <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                            <i className="ri-star-fill text-xs"></i> Öne Çıkan
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-20 bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center">
                      <i className="ri-building-4-line text-stone-400 text-3xl"></i>
                    </div>
                  )}
                  <div className="p-4">
                    {!city.image && (
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-stone-900 text-sm">{city.name}</h4>
                          <p className="text-xs text-stone-400">{city.region}</p>
                        </div>
                        {city.featured && (
                          <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                            <i className="ri-star-fill text-xs"></i> Öne Çıkan
                          </span>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-stone-500 line-clamp-2 mb-3">{city.shortDesc}</p>
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span className="flex items-center gap-1 text-xs text-stone-400">
                        <i className="ri-sun-line text-amber-500"></i> {city.bestSeason}
                      </span>
                      {city.highlights && (
                        <span className="flex items-center gap-1 text-xs text-stone-400">
                          <i className="ri-map-pin-line text-emerald-500"></i>
                          {city.highlights.split(",").slice(0, 2).join(", ")}
                          {city.highlights.split(",").length > 2 && ` +${city.highlights.split(",").length - 2}`}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-stone-100">
                      <p className="text-xs text-stone-300">{new Date(city.createdAt).toLocaleDateString("tr-TR")}</p>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => editCity(city)}
                          className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(`city-${city.id}`)}
                          className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContentManager;
